import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  count, evidenceLabel, formatDay, formatSlotDay, listPartners, listProviderProspects, listRowLimit, loadAllRows, orDash, outreachReasonLabel, outreachStages,
  partnerStatus, phoneHref, referralForm, referralSortValue, referralTypeLabel, teamStatus, unflagOutreachProvider, webLink,
} from './adminData.js';
import { PartnerSection } from './Partner.jsx';
import { AccommodationCell, SupportCell, accommodationSortValue, isTagged, supportSortValue } from './ProviderSupport.jsx';
import { filterChoices, filterProviders, identityFilters, standingFilters } from './providerFilters.js';
import { useSort } from './SortableTable.jsx';

const noRows = [];
const asList = (value) => (Array.isArray(value) ? value : []);

// The council column is sorted by the first council's name (or, for a provider with no single council, its broad area).
const councilNames = (row) => {
  const names = asList(row.councils).map((council) => council.name || council.code);
  return names.length > 0 ? names : asList(row.regions);
};
const stages = new Map(outreachStages.map(([value, words, tone], rank) => [value, { words, tone, rank }]));
const sortBy = {
  provider: { value: (row) => row.organisation },
  area: { value: councilNames },
  support: { value: supportSortValue },
  accommodation: { value: accommodationSortValue },
  // The furthest along first (a reply, then emailed…); dropped providers last.
  outreach: { value: (row) => (row.outreach_stage === 'dropped' ? -1 : stages.get(row.outreach_stage)?.rank ?? 0), first: 'desc' },
  referral: { value: referralSortValue, first: 'desc' },
};

/** An email address that opens the staff member's mail app. */
export function EmailLink({ value }) {
  const email = String(value ?? '').trim();
  return email ? <a className="admin-ext" href={`mailto:${email}`}>{email}</a> : '–';
}

/** A phone number that can be dialled. */
export function PhoneLink({ value }) {
  const phone = String(value ?? '').trim();
  const href = phoneHref(phone);
  if (!phone) return '–';
  return href ? <a className="admin-ext admin-nowrap" href={href}>{phone}</a> : <span className="admin-nowrap">{phone}</span>;
}

function Council({ row, onOpen }) {
  const councils = asList(row.councils);
  if (councils.length > 0) {
    return councils.map((council, index) => (
      <Fragment key={council.code}>
        {index > 0 && ', '}
        <button type="button" className="admin-link" onClick={() => onOpen?.({ tab: 'councils', council: council.code })}>{council.name || council.code}</button>
      </Fragment>
    ));
  }
  const regions = asList(row.regions);
  return regions.length > 0 ? regions.join(', ') : '–';
}

function SelfReferral({ row }) {
  if (!row.self_referral) return '–';
  const source = webLink(row.self_referral_source);
  return source
    ? <a className="admin-status is-good" href={source.href} target="_blank" rel="noreferrer" title="Open the page that says people can refer themselves">Yes</a>
    : <span className="admin-status is-good">Yes</span>;
}

/**
 * Whether the provider's website shows its team: "Shows the team" and how many people, opening the page it was found on; a page
 * that names one person or nobody also opens. Checked automatically, so the link is there to look for yourself.
 */
function TeamPage({ row }) {
  const status = teamStatus(row.team_status);
  if (!status) return '–';
  const page = webLink(row.team_page_url);
  const people = Number(row.team_people) || 0;
  return (
    <>
      {page
        ? <a className={`admin-status is-${status.tone}`} href={page.href} target="_blank" rel="noreferrer" title={`Open ${page.href}`}>{status.label}</a>
        : <span className={`admin-status is-${status.tone}`}>{status.label}</span>}
      {row.team_status === 'team' && <span className="admin-sub">{`${count(people)} people named`}</span>}
    </>
  );
}

/**
 * How someone is referred, from the provider's website: an online form, a form to download, a referral page with no form (asking
 * for an email or a call) or none found, opening the form or page; and under it who it is for, when the page says.
 */
function ReferralForm({ row }) {
  const form = referralForm(row.referral_form);
  if (!form) return '–';
  const page = webLink(row.referral_url);
  const types = asList(row.referral_types);
  return (
    <>
      {page
        ? <a className={`admin-status is-${form.tone}`} href={page.href} target="_blank" rel="noreferrer" title={`Open ${page.href}`}>{form.label}</a>
        : <span className={`admin-status is-${form.tone}`}>{form.label}</span>}
      {row.referral_form !== 'none' && (
        <span className="admin-sub">{types.length > 0 ? types.map(referralTypeLabel).join(' · ') : "Doesn't say who for"}</span>
      )}
    </>
  );
}

/**
 * Where the provider stands with us, in a few words: their reply ("Accepting referrals"), "Emailed · Tue 21 Oct", "Booked · Thu 30 Oct",
 * dropped, in the plan, or not contacted.
 */
function Stage({ row }) {
  const stage = row.outreach_stage || 'not_contacted';
  if (stage === 'replied') {
    const reply = partnerStatus(row.partner_status);
    return <span className={`admin-status is-${reply.tone}`}>{reply.label}</span>;
  }
  const { words, tone } = stages.get(stage) || { words: String(stage), tone: 'neutral' };
  if (stage === 'emailed') return <span className={`admin-status is-${tone}`}>{`Emailed · ${formatSlotDay(row.emailed_at)}`}</span>;
  if (stage === 'booked') return <span className={`admin-status is-${tone}`}>{`Booked · ${formatSlotDay(row.booked_for)}`}</span>;
  if (stage === 'dropped') return <span className={`admin-status is-${tone}`}>Dropped</span>;
  return <span className={`admin-status is-${tone}`}>{words}</span>;
}

/**
 * The outreach history under a provider: its stage, when the first email went out or the next one is booked, when it replied, whether
 * the outreach desk wrote before, and, for a provider dropped from Naima's outreach (and so left out of future schedules), why, when
 * and any note, with Undo to take the flag off.
 */
function OutreachHistory({ row, busy, onUndo }) {
  const note = String(row.outreach_excluded_note ?? '').trim();
  return (
    <>
      <Stage row={row}/>
      {row.emailed_at && <span className="admin-sub">{`First email ${formatSlotDay(row.emailed_at)}`}</span>}
      {row.booked_for && <span className="admin-sub">{`Next email booked for ${formatSlotDay(row.booked_for)}`}</span>}
      {row.replied_on && <span className="admin-sub">{`Replied ${formatDay(row.replied_on)}`}</span>}
      {row.previously_contacted && (
        <span className="admin-sub">{`Contacted before this list${row.last_contacted_at ? ` · ${formatDay(row.last_contacted_at)}` : ''}`}</span>
      )}
      {row.outreach_excluded_at && (
        <>
          <span className="admin-status is-bad">{`Dropped · ${outreachReasonLabel(row.outreach_excluded_reason)}`}</span>
          <span className="admin-sub">{`${formatDay(row.outreach_excluded_at)}${note ? ` · ${note}` : ''}`}</span>
          <button type="button" className="button button-secondary admin-undo" disabled={busy} title={`Put ${row.organisation} back in the list for future schedules`} onClick={() => onUndo(row)}>Undo</button>
        </>
      )}
    </>
  );
}

/** Everything else about a provider, opened under its line: how to reach it, who it supports and why, and where it stands. */
function Details({ row, onOpen, busy, onUndo }) {
  const site = webLink(row.website);
  const towns = asList(row.service_areas);
  return (
    <div className="prov-details">
      <dl>
        <dt>Website</dt>
        <dd>{site ? <a className="admin-ext" href={site.href} target="_blank" rel="noreferrer">{site.domain}</a> : '–'}</dd>
        <dt>Email</dt><dd><EmailLink value={row.email}/></dd>
        <dt>Phone</dt><dd><PhoneLink value={row.phone}/></dd>
        <dt>Office</dt><dd>{orDash(row.office_address)}</dd>
        <dt>Councils</dt><dd><Council row={row} onOpen={onOpen}/></dd>
        <dt>Towns</dt><dd>{towns.length > 0 ? towns.join(', ') : '–'}</dd>
        <dt>Nation</dt><dd>{asList(row.nations).join(', ') || '–'}</dd>
      </dl>
      <dl>
        <dt>Who they support</dt><dd><SupportCell row={row}/></dd>
        <dt>Accommodation</dt><dd><AccommodationCell row={row}/></dd>
      </dl>
      <dl>
        <dt>Outreach</dt><dd><OutreachHistory row={row} busy={busy} onUndo={onUndo}/></dd>
        <dt>Self-referral</dt><dd><SelfReferral row={row}/></dd>
        <dt>Referral form</dt><dd><ReferralForm row={row}/></dd>
        <dt>Team page</dt><dd><TeamPage row={row}/></dd>
        <dt>Evidence</dt><dd>{evidenceLabel(row.evidence_level)}</dd>
        <dt>Priority</dt><dd>{orDash(row.priority)}</dd>
        <dt>Added</dt><dd>{formatDay(row.added_on)}</dd>
      </dl>
    </div>
  );
}

/** Text cut to one line, all of it on hover. */
function Clip({ text }) {
  return text ? <span className="prov-clip" title={text}>{text}</span> : '–';
}

/** The labels in one line: a dash when the provider hasn't been read yet, "Not stated" when nothing was found. */
function labelsLine(row, labelsOf) {
  if (!isTagged(row)) return '';
  return labelsOf(row).join(' · ') || 'Not stated';
}

function areaLine(row) {
  const names = councilNames(row);
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0] || '';
}

/** A row of dropdowns under a heading; a dropdown with a choice made stands out. */
function FilterRow({ title, filters, choices, chosen, onChoose }) {
  return (
    <div className="prov-filter-row" role="group" aria-label={title}>
      <span className="prov-filter-title">{title}</span>
      {filters.map((filter) => (
        <select key={filter.key} aria-label={filter.label} className={chosen[filter.key] ? 'is-chosen' : undefined} value={chosen[filter.key] || ''}
          onChange={(event) => onChoose(filter.key, event.target.value)}>
          <option value="">{filter.any}</option>
          {(choices[filter.key] || []).map(([value, words, number]) => <option key={value} value={value}>{`${words} (${count(number)})`}</option>)}
        </select>
      ))}
    </div>
  );
}

/**
 * Housing providers we are reaching out to, from the outreach research. They are not accounts on Resident Service 360.
 * With `council` set (an ONS code) only that council's providers are listed. Every provider is read once; the search and the
 * dropdowns (who they are, and where they stand with us) then filter in the browser. Each provider is one line, and clicking
 * it opens the rest, including, for a provider who answered Naima, the partner details (where referrals go, who to talk to,
 * their reply) and the buttons to record or change them.
 */
export default function ProviderProspects({ council = null, onOpen, heading = true }) {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState({});
  // The dropdowns stay folded away behind the Filters button until asked for.
  const [showFilters, setShowFilters] = useState(false);
  const [opened, setOpened] = useState(() => new Set());
  const [list, setList] = useState({ rows: null, loadedAt: 0, capped: false, error: '' });
  // The provider whose flag is being taken off, what that answered and what went wrong.
  const [undoing, setUndoing] = useState(null);
  const [notice, setNotice] = useState('');
  const [undoError, setUndoError] = useState('');
  // Providers who answered Naima, by provider id, for the partner details under each line.
  const [partners, setPartners] = useState({ byProvider: new Map(), error: '' });
  // Every request takes a number; only the newest one is allowed to change the screen.
  const latest = useRef(0);

  const everyone = list.rows || noRows;
  const shown = useMemo(() => filterProviders(everyone, chosen, query, list.loadedAt), [everyone, chosen, query, list.loadedAt]);
  const choices = useMemo(() => filterChoices(everyone, chosen, query, list.loadedAt), [everyone, chosen, query, list.loadedAt]);
  const { rows, header } = useSort(shown, sortBy);
  const identity = council ? identityFilters.filter((filter) => filter.key !== 'area') : identityFilters;
  const chosenCount = Object.values(chosen).filter(Boolean).length;
  const filtering = query.trim() !== '' || chosenCount > 0;

  // Reads the whole list, 200 housing providers at a time.
  const load = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const all = await loadAllRows(
        (page) => listProviderProspects(supabase, { council, ...page }),
        { isCurrent: () => ticket === latest.current },
      );
      if (!all || ticket !== latest.current) return;
      setList({ rows: all.rows, loadedAt: Date.now(), capped: all.capped, error: '' });
    } catch (cause) {
      if (ticket !== latest.current) return;
      setList((current) => ({ ...current, rows: current.rows || [], error: cause.message }));
    }
  }, [council]);

  useEffect(() => {
    void load();
    return () => { latest.current += 1; };
  }, [load]);

  const loadPartners = useCallback(async () => {
    try {
      const list = await listPartners(supabase);
      setPartners({ byProvider: new Map(asList(list?.rows).map((partner) => [partner.provider_id, partner])), error: '' });
    } catch (cause) {
      setPartners((current) => ({ ...current, error: `Partner details: ${cause.message}` }));
    }
  }, []);

  useEffect(() => { void loadPartners(); }, [loadPartners]);

  // A partner was recorded, changed or taken off: its line and its details may both have changed.
  const partnerChanged = () => { void load(); void loadPartners(); };

  const { capped, error } = list;

  const choose = (key, value) => setChosen((current) => ({ ...current, [key]: value }));
  const clear = () => { setQuery(''); setChosen({}); };
  const toggle = (id) => setOpened((current) => {
    const next = new Set(current);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  async function undo(row) {
    setUndoing(row.id);
    setNotice('');
    setUndoError('');
    try {
      await unflagOutreachProvider(supabase, row.id);
      setNotice(`${row.organisation} is back in the list for future schedules.`);
    } catch (cause) {
      setUndoError(cause.message || 'Could not undo that. Please try again.');
    } finally {
      setUndoing(null);
    }
    // Either way the list on screen may be out of date.
    void load();
  }

  return (
    <>
      {heading && <h2 className="admin-section">Housing providers we're reaching out to</h2>}
      <div className="prov-filters">
        <div className="prov-search">
          <input type="search" aria-label="Search housing providers" value={query} onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by provider, council, town or email"/>
          <button type="button" className={`prov-filter-toggle${chosenCount > 0 ? ' is-chosen' : ''}`} aria-expanded={showFilters}
            onClick={() => setShowFilters((open) => !open)}>
            {chosenCount > 0 ? `Filters · ${chosenCount}` : 'Filters'}
          </button>
          {filtering && <button type="button" className="admin-link" onClick={clear}>Clear filters</button>}
        </div>
        {showFilters && (
          <>
            <FilterRow title="Who they are" filters={identity} choices={choices} chosen={chosen} onChoose={choose}/>
            <FilterRow title="Where they stand" filters={standingFilters} choices={choices} chosen={chosen} onChoose={choose}/>
          </>
        )}
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {partners.error && <p className="mod-error" role="alert">{partners.error}</p>}
      {undoError && <p className="mod-error" role="alert">{undoError}</p>}
      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {list.rows === null
        ? <p>Loading housing providers…</p>
        : (
          <>
            {rows.length === 0
              ? (!error && <p className="admin-empty">No housing providers match.</p>)
              : (
                <div className="mod-table-wrap prov-table">
                  <table className="mod-audit prov-list">
                    <thead>
                      <tr>
                        {header('provider', 'Provider')}{header('area', 'Area')}{header('support', 'Who they support')}{header('accommodation', 'Accommodation')}
                        {header('outreach', 'Outreach')}{header('referral', 'Referral form')}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const open = opened.has(row.id);
                        return (
                          <Fragment key={row.id}>
                            <tr className={`prov-row${open ? ' is-open' : ''}`} onClick={(event) => { if (!event.target.closest('a, button')) toggle(row.id); }}>
                              <td>
                                <button type="button" className="prov-name" aria-expanded={open} onClick={() => toggle(row.id)}>
                                  <Clip text={row.organisation}/>
                                </button>
                              </td>
                              <td><Clip text={areaLine(row)}/></td>
                              <td><Clip text={labelsLine(row, supportSortValue)}/></td>
                              <td><Clip text={labelsLine(row, accommodationSortValue)}/></td>
                              <td><Stage row={row}/></td>
                              <td>{referralForm(row.referral_form)?.label ?? '–'}</td>
                            </tr>
                            {open && (
                              <tr className="prov-detail">
                                <td colSpan={6}>
                                  <Details row={row} onOpen={onOpen} busy={undoing !== null} onUndo={undo}/>
                                  <PartnerSection provider={row} partner={partners.byProvider.get(row.id)} onChanged={partnerChanged}/>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            {capped && <p className="mod-note">{`Showing the first ${count(listRowLimit)}.`}</p>}
          </>
        )}
    </>
  );
}
